import React, { useState, useMemo, useEffect } from 'react';
import { Text, View, TouchableOpacity, Modal, Platform, InteractionManager } from 'react-native';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { getImageSource, saveFile, renderManipulatedImage } from '@/utils/FileManager';
import useLoader from '@/utils/hooks/useLoader';
import Toast from 'react-native-toast-message';
import { Image } from 'expo-image';
import { updateAvatarUpdatedAt } from '@/data/people';
import { queryClient } from '@/data/client';
import SheetModal from '@/components/General/SheetModal';
import { Tooltip } from '@/components/General/Tooltip';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { withUnistyles } from 'react-native-unistyles';
import Unicon from '../General/Unicon';
import CameraFeed from '../General/CameraFeed';
import { useRouter } from 'expo-router';

const UniImage = withUnistyles(Image);

function Avatar({ size, profile, directory = "avatars/", disableBorder = false, borderColor = null, emptyIcon = "person", editable = false, onPress = null, onChange = null, tooltipExtraText = null, testID = null }) {
    const [fromLibraryOpen, setFromLibraryOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [hasCamera, setHasCamera] = useState(true);
    const loader = useLoader();
    const router = useRouter();
    const { rt: { fontScale } } = useUnistyles();
    const scaledSize = Math.max(size, Math.ceil(size * fontScale));

    useEffect(() => {
        const checkCamera = async () => {
            if (Platform.OS !== 'web') {
                setHasCamera(true);
                return;
            }
            try {
                if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    setHasCamera(devices.some(d => d.kind === 'videoinput'));
                } else {
                    setHasCamera(false);
                }
            } catch {
                setHasCamera(false);
            }
        };
        checkCamera();
    }, []);

    // Subscribe only to organizationID (a stable primitive) so every mounted
    // Avatar doesn't re-render on each loggedInUser cache update.
    const { data: organizationID } = Query.people.loggedInUser({options: {select: (user) => user?.organizationID}})

    // Fire-and-forget (was a useMutation whose onSuccess only invalidated
    // queries). Reads loggedInUser from the cache at call time — no
    // subscription needed for the id comparison.
    const updateAvatarTimestamp = ({personID, storageKey}) => {
        updateAvatarUpdatedAt({personID, storageKey})
            .then((data) => {
                if(!data?.id) return;

                const user = queryClient.getQueryData(QueryKeys.people.loggedInUser());
                if(data.id == user?.id){
                    queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()});
                }

                queryClient.invalidateQueries({queryKey: QueryKeys.people.byId(data.id)});
                queryClient.invalidateQueries({queryKey: QueryKeys.people.all()});
            })
            .catch(() => {});
    }

    const imageKey = useMemo(() => {
        if(directory == "avatars/"){
            return profile?.avatarStorageKey || ""
        }

        return profile?.key ? profile?.key : `${organizationID}/${directory}${profile?.id}.jpg`
    }, [profile?.id, profile?.key, profile?.avatarStorageKey, profile?.avatarUpdatedAt, organizationID, directory])

    const source = useMemo(() => {
        if (profile?.uri) return { uri: profile.uri };
        if (!imageKey) return null;
        return getImageSource(imageKey, profile?.avatarUpdatedAt ? `${profile?.id}+${profile.avatarUpdatedAt}` : undefined) || {};
    }, [imageKey, profile?.id, profile?.avatarUpdatedAt, profile?.uri]);

    // `recyclingKey` is expo-image's hook for recycled lists (LegendList
    // `recycleItems`, Fabric view pooling): when the key changes the previous
    // image is cleared before the new source renders. Without it a recycled
    // row keeps showing the previous person's avatar — especially when the new
    // person has no avatar (null source never fires onLoad, so the stale
    // image node never gets replaced on web).
    const UserAvatar = useMemo(() => {
        return <UniImage
            // transition={100}
            recyclingKey={profile?.id != null ? `${directory}${profile.id}` : undefined}
            source={source}
            style={styles.avatar(scaledSize)}
        />
    }, [source, scaledSize, directory, profile?.id])

    if(!profile) return null;

    const choosePhoto = async (newPhoto = false) => {
        setFromLibraryOpen(false);

        if (newPhoto && Platform.OS === 'web') {
            setCameraOpen(true);
            return;
        }

        InteractionManager.runAfterInteractions(async () => {
            const options = {
                mediaTypes: ['images'],
                allowsEditing: true,
                aspect: [3, 3],
                quality: 1,
            }

            try{
                const permission = newPhoto ? await ImagePicker.requestCameraPermissionsAsync() : await ImagePicker.requestMediaLibraryPermissionsAsync()

                if(!permission || !permission.granted){
                    throw Error()
                }

                let result = newPhoto ? await ImagePicker.launchCameraAsync(options) : await ImagePicker.launchImageLibraryAsync(options)

                if (!result.canceled) {
                    await processAndSaveImage(result.assets[0].uri);
                }
            }
            catch{
                loader.disable();
                Toast.show({type:'error', text2:`There was a problem opening ${newPhoto ? "the camera" : "the photo library"}. Try checking your camera permissions in your phone settings`})
            }
        });
    };

    const processAndSaveImage = async (imageUri) => {
        try {
            loader.enable();
            
            if(!onChange){
                if(profile.id){
                    const finalImage = await renderManipulatedImage({uri: imageUri, compress: 0.2, resize: { width: 500 }, context: 'Avatar.processAndSaveImage'});

                    const {key} = await saveFile(finalImage?.uri || imageUri, `${organizationID}/${directory}${profile.id}.jpg`);

                    if(directory == "avatars/"){
                        updateAvatarTimestamp({personID: profile.id, storageKey: key});
                    }
                }
            }
            else{
                onChange({ uri: imageUri });
            }
            
            loader.disable();
        } catch {
            loader.disable();
            Toast.show({type:'error', text2:'There was a problem saving the image'})
        }
    };

    const chooseFile = async () => {
        setFromLibraryOpen(false);

        InteractionManager.runAfterInteractions(async () => {
            try {
                const result = await DocumentPicker.getDocumentAsync({
                    type: Platform.OS === 'web'
                        ? ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']
                        : ['image/*'],
                    copyToCacheDirectory: true,
                });

                if (result.canceled || !result.assets?.length) return;

                const asset = result.assets[0];
                const allowedExts = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
                const ext = asset.name?.split('.').pop()?.toLowerCase();

                if (!allowedExts.includes(ext)) {
                    Toast.show({ type: 'error', text2: 'Please select a JPG, PNG, WebP, or HEIC image' });
                    return;
                }

                await processAndSaveImage(asset.uri);
            } catch {
                Toast.show({ type: 'error', text2: 'There was a problem selecting the file' });
            }
        });
    };

    const handleCameraCapture = async (photo) => {
        setCameraOpen(false);
        if (photo?.uri) {
            await processAndSaveImage(photo.uri);
        }
    };

    return (
        <View style={styles.container(scaledSize, borderColor, disableBorder)}>
            {!source && <View style={styles.initialsContainer}>
                {(!profile?.firstName || !profile?.lastName)
                    ? <Unicon name={emptyIcon} colorVariant="onPrimary" size={scaledSize * 0.55}/>
                    : <Text style={styles.initials(scaledSize)}>{profile.firstName?.[0] + profile.lastName?.[0]}</Text>
                }
            </View>}

            <Tooltip hitslop={0} testID={testID} title={profile?.firstName ? profile?.firstName + " " + profile?.lastName : ""} subtitle={tooltipExtraText} onPress={() => {
                if(onPress){
                    onPress()
                    return;
                }
                
                if(editable) {
                    setFromLibraryOpen(true);
                    return;
                }
                
                router.push("/people/" + profile?.id)
            }}>
                {UserAvatar}
            </Tooltip>

            {editable && 
                <View style={styles.editContainer(scaledSize)}>
                    <Unicon name="pencil" size={Math.max(scaledSize * 0.18, 9)} style={{color: 'white'}} onPress={() => setFromLibraryOpen(true)}/>
                </View>
            }
            
            <SheetModal isOpen={fromLibraryOpen} onClose={() => setFromLibraryOpen(false)} title={profile?.firstName + " " + profile?.lastName} snapPoints={[420]} >
                {source && (
                    <View style={styles.modalImageContainer}>
                        <View style={styles.modalImageRing}>
                            <UniImage source={source} style={styles.modalImage} />
                        </View>
                    </View>
                )}
                <View style={styles.modalActions}>
                    {hasCamera && (
                        <TouchableOpacity onPress={() => choosePhoto(true)} style={styles.modalItem}>
                            <View style={styles.modalIconWrap}>
                                <Unicon name="camera" size={22} colorVariant="primary" />
                            </View>
                            <Text style={styles.modalText}>Take a Photo</Text>
                            <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                        </TouchableOpacity>
                    )}

                    {Platform.OS !== 'web' && (
                        <TouchableOpacity onPress={() => choosePhoto()} style={styles.modalItem}>
                            <View style={styles.modalIconWrap}>
                                <Unicon name="images" size={22} colorVariant="primary" />
                            </View>
                            <Text style={styles.modalText}>Choose from Library</Text>
                            <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                        </TouchableOpacity>
                    )}

                    <TouchableOpacity onPress={() => chooseFile()} style={styles.modalItem}>
                        <View style={styles.modalIconWrap}>
                            <Unicon name="folder-outline" size={22} colorVariant="primary" />
                        </View>
                        <Text style={styles.modalText}>Choose File</Text>
                        <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                    </TouchableOpacity>
                </View>
            </SheetModal>

            <Modal statusBarTranslucent={true} visible={cameraOpen} animationType="slide" onRequestClose={() => setCameraOpen(false)}>
                <CameraFeed 
                    onCapture={handleCameraCapture} 
                    onClose={() => setCameraOpen(false)} 
                    facingMode="user"
                />
            </Modal>
         </View>
    )
}

const styles = StyleSheet.create(({colors, fonts, roundness}, rt) => ({
    avatar: (size) => ({
        width:size, 
        height:size,
        alignItems:"center", 
        justifyContent:"center", 
        borderRadius:80
    }),
    initialsContainer: {
        backgroundColor:colors.primary,
        alignItems:"center",
        justifyContent:"center", 
        height:"100%",
        width:"100%",
        borderRadius:80,
        position:"absolute",
        top:0,
        left:0,
    },
    container: (size, borderColor, disableBorder) => ({
        borderRadius:80, 
        boxShadow:"0px 0px 8px -2px rgba(0, 0, 0, 0.2)", 
        alignItems:'center', 
        justifyContent:'center',
        height:disableBorder ? size : size+4, 
        width:disableBorder ? size : size+4,
        borderWidth:borderColor ? size * 0.1 : 0, 
        borderColor:borderColor ? colors[borderColor] : "transparent", 
    }),
    initials: (size) => ({
        fontSize:size/2.4, 
        fontFamily:fonts.light, 
        color:colors.onPrimary
    }),
    editContainer: (size) => ({
        position:"absolute", 
        bottom:-1, 
        right:-1, 
        alignItems:"center", 
        justifyContent:"center", 
        height:Math.max(size * 0.3, 14), 
        width:Math.max(size * 0.3, 14), 
        borderRadius:20, 
        overflow:"hidden",
        backgroundColor:rt.themeName === "dark" ? "rgba(0,0,0,0.8)" : "rgba(0,0,0,0.6)"
    }),
    modalImageContainer:{alignItems:"center", justifyContent:"center", marginTop:10, marginBottom:25},
    modalImageRing:{
        padding:4,
        borderRadius:100,
        backgroundColor:colors.surface,
        boxShadow:'0px 4px 16px rgba(0,0,0,0.12)'
    },
    modalImage:{width:180, height:180, borderRadius:90},
    modalActions:{
        backgroundColor:colors.surface,
        borderRadius:roundness.md,
        overflow:'hidden',
        width:'100%',
        maxWidth:400,
        marginHorizontal:'auto'
    },
    modalItem:{flexDirection:"row", alignItems:"center", paddingHorizontal:16, paddingVertical:14},
    modalIconWrap:{
        width:36,
        height:36,
        borderRadius:18,
        alignItems:"center",
        justifyContent:"center",
        backgroundColor:colors.primaryContainer || colors.primary+'15',
        marginRight:14
    },
    modalText:{color: colors.text, fontSize:17, fontFamily:fonts.regular, flex:1},
    modalDivider:{height:1, backgroundColor:colors.outline || colors.border || 'rgba(0,0,0,0.08)', marginHorizontal:16}
}))

export default React.memo(Avatar)